import { FRAMED_SERVICE_SECTION_OPTIONS } from '../../capabilities/service/ServiceBlock';
import type { SkuResourceModulesState } from '../../platform/scene-layout/types';
import { buildDefaultDirectResourceModules } from '../../platform/scene-layout/utils/resolveModuleDefaults';
import type { DeploySceneConfig } from '../../platform/scenes/types';
import { CI_MODULES } from './modules';

export const CI_ALL_MODULES = CI_MODULES.map((m) => m.key);

/** SKU 创建可见模块（基础信息 / 网络由外层或下单时填写） */
export const CI_SKU_MODULES = ['systemSetting', 'resource', 'startupCommandArgs', 'envVars', 'service', 'storage', 'dns'] as const;

export type CiSkuModuleKey = (typeof CI_SKU_MODULES)[number];

export const CI_SKU_MODULE_LIST: string[] = [...CI_SKU_MODULES];

export function isCiSkuModuleKey(moduleKey: string): moduleKey is CiSkuModuleKey {
  return (CI_SKU_MODULES as readonly string[]).includes(moduleKey);
}

export function filterCiSkuModules<T extends { key: string }>(modules: readonly T[]): T[] {
  const set = new Set<string>(CI_SKU_MODULES);
  return modules.filter((mod) => set.has(mod.key));
}

export interface CiDirectCreateStep {
  key: string;
  title: string;
  modules: string[];
}

/** 容器实例直建合并「资源配置」步可见模块 */
export const CI_DIRECT_MERGED_MODULES = [
  'baseInfo',
  'network',
  'systemSetting',
  'resource',
  'startupCommandArgs',
  'envVars',
  'service',
  'storage',
  'dns',
] as const;

export type CiDirectMergedModuleKey = (typeof CI_DIRECT_MERGED_MODULES)[number];

export function filterCiDirectMergedModules<T extends { key: string }>(modules: readonly T[]): T[] {
  const set = new Set<string>(CI_DIRECT_MERGED_MODULES);
  return modules.filter((mod) => set.has(mod.key));
}

/** 下单页模块：直建合并去掉 baseInfo */
export const CI_ORDER_MODULES = CI_DIRECT_MERGED_MODULES.filter(
  (key): key is Exclude<CiDirectMergedModuleKey, 'baseInfo'> => key !== 'baseInfo',
);

const CI_SCENE_CAPABILITY_DEFAULTS = {
  compute: {
    gpuVariant: 'container',
    showBandwidth: true,
    showSharedMemory: true,
    showNic: true,
    nicsField: 'network.nics',
    clearGpuOnArchitectureChange: false,
  },
  image: {
    showPullAuth: true,
  },
} as const;

export const CI_SCENES: Record<'direct' | 'sku' | 'order', DeploySceneConfig> = {
  direct: {
    modules: [...CI_ALL_MODULES],
    capabilityOptions: {
      ...CI_SCENE_CAPABILITY_DEFAULTS,
      service: FRAMED_SERVICE_SECTION_OPTIONS,
    },
    linkage: true,
  },
  sku: {
    modules: [...CI_SKU_MODULES],
    capabilityOptions: {
      ...CI_SCENE_CAPABILITY_DEFAULTS,
      service: FRAMED_SERVICE_SECTION_OPTIONS,
    },
    linkage: true,
  },
  order: {
    modules: [...CI_ORDER_MODULES],
    capabilityOptions: {},
    linkage: true,
  },
};

export function resolveCiSceneConfig(scene?: 'direct' | 'sku' | 'order'): DeploySceneConfig {
  return CI_SCENES[scene ?? 'direct'];
}

/** 容器实例直建：单步「资源配置」 */
export const CI_DIRECT_CREATE_STEPS: CiDirectCreateStep[] = [
  { key: 'resourceConfig', title: '资源配置', modules: [...CI_DIRECT_MERGED_MODULES] },
];

function hasDnsConfig(dnsConfig: unknown): boolean {
  if (!dnsConfig || typeof dnsConfig !== 'object') return false;
  const record = dnsConfig as Record<string, unknown>;
  const listKeys = ['nameservers', 'searches', 'options'] as const;
  return listKeys.some((key) => {
    const list = record[key];
    return Array.isArray(list) && list.length > 0;
  });
}

/** 编辑回显：按已有表单数据推断直建模块勾选状态 */
export function buildCiDirectResourceModulesFromForm(values: Record<string, unknown>): SkuResourceModulesState {
  const defaults = buildDefaultDirectResourceModules('ContainerInstance');
  const volumes = values.volumes;
  const network = (values.resource as Record<string, unknown> | undefined)?.network;
  const basicImage = (values.basic as Record<string, unknown> | undefined)?.image as Record<string, unknown> | undefined;
  const hasVolumes = Array.isArray(volumes) && volumes.length > 0;
  const hasDns = hasDnsConfig(network && typeof network === 'object' ? (network as Record<string, unknown>).dnsConfig : undefined);
  const hasStartupConfig =
    Boolean(String(basicImage?.command ?? '').trim()) ||
    (Array.isArray(basicImage?.startupArgs) && basicImage!.startupArgs.length > 0) ||
    Boolean(String(basicImage?.args ?? '').trim());
  const hasEnv = Array.isArray(basicImage?.env) && basicImage!.env.length > 0;
  const hasServices =
    Array.isArray((network as Record<string, unknown> | undefined)?.services) &&
    ((network as Record<string, unknown>).services as unknown[]).length > 0;
  const scheduling = values._containerDeployInstanceScheduling;
  const hasScheduling =
    scheduling &&
    typeof scheduling === 'object' &&
    (Number((scheduling as Record<string, unknown>).minCpuCores) > 0 ||
      Number((scheduling as Record<string, unknown>).minMemoryGi) > 0 ||
      (Array.isArray((scheduling as Record<string, unknown>).gpus) &&
        ((scheduling as Record<string, unknown>).gpus as unknown[]).length > 0));

  return {
    ...defaults,
    ...(hasVolumes ? { storage: true } : {}),
    ...(hasDns ? { dns: true } : {}),
    ...(hasStartupConfig ? { startupCommandArgs: true } : {}),
    ...(hasEnv ? { envVars: true } : {}),
    ...(hasServices ? { service: true } : {}),
    ...(hasScheduling ? { resource: true } : {}),
  };
}
